/**
 * useLocaleFormat — the one entry point components use to show dates, times,
 * numbers and measured values in the active app language. English renders US
 * conventions (MM/DD/YYYY, 12-hour clock, lb/ft-in/°F); other languages their
 * own, metric. Stored values stay ISO dates and SI units.
 *
 * An explicit unit choice in the unit preferences wins over the language, here
 * exactly as in the unit inputs — otherwise the same weight showed in kg in the
 * input and in lb on the page around it.
 */

import { useMemo } from "react";
import { useI18nStore } from "../stores/i18nStore";
import { useChosenSystems } from "../stores/unitPreferencesStore";
import {
  type DateStyle,
  type DateValue,
  formatDate,
  formatDateTime,
  formatNumber,
  formatTime,
  getLocaleString,
} from "../utils/dateLocalization";
import {
  fromDisplayValue,
  formatQuantity,
  getUnitSystem,
  type Quantity,
  toDisplayValue,
  unitLabel,
} from "../utils/unitLocalization";

export function useLocaleFormat() {
  const language = useI18nStore((s) => s.language);
  const systems = useChosenSystems();
  return useMemo(
    () => ({
      language,
      locale: getLocaleString(language),
      unitSystem: getUnitSystem(language),
      /** The system one quantity is shown in: the explicit choice, else the language. */
      systemOf: (q: Quantity) => systems?.[q] ?? getUnitSystem(language),
      date: (v: DateValue, style?: DateStyle) => formatDate(v, language, style),
      time: (v: DateValue) => formatTime(v, language),
      dateTime: (v: DateValue, style?: DateStyle) =>
        formatDateTime(v, language, style),
      number: (v: number, maxFractionDigits?: number) =>
        formatNumber(v, language, maxFractionDigits),
      quantity: (q: Quantity, stored: number | null | undefined) =>
        formatQuantity(q, stored, language, systems),
      unit: (q: Quantity) => unitLabel(q, language, systems),
      toDisplay: (q: Quantity, stored: number) =>
        toDisplayValue(q, stored, language, systems),
      fromDisplay: (q: Quantity, entered: number) =>
        fromDisplayValue(q, entered, language, systems),
    }),
    [language, systems],
  );
}

export type LocaleFormat = ReturnType<typeof useLocaleFormat>;
